/**
 * LocalizationForm is a custom HTMLElement that provides a dropdown list for language and country selection.
 * It allows users to select a value from the list, submit the form, and manage the visibility of the list.
 */
class LocalizationForm extends window.HTMLElement {
  constructor() {
    super()

    // Store references to form elements
    this.elements = {
      input: this.querySelector(
        'input[name="language_code"], input[name="country_code"]'
      ),
      button: this.querySelector('button'),
      list: this.querySelector('ul'),
    }

    // Attach event listeners to form elements
    this.elements.button.addEventListener('click', this.toggleList.bind(this))
    this.elements.button.addEventListener(
      'focusout',
      this.onButtonFocusOut.bind(this)
    )
    this.elements.list.addEventListener(
      'focusout',
      this.onListFocusOut.bind(this)
    )
    this.addEventListener('keyup', this.onLocalizationFormKeyUp.bind(this))

    // Attach event listeners to list items
    this.querySelectorAll('a').forEach((item) =>
      item.addEventListener('click', this.onItemClick.bind(this))
    )

    // Attach global click event listener to the body
    document.body.addEventListener('click', this.onBodyClick.bind(this))
  }

  /**
   * Hide the dropdown list and update the button's attributes and classes.
   */
  hideList() {
    this.elements.button.setAttribute('aria-expanded', 'false')
    this.elements.list.setAttribute('hidden', true)
    this.elements.button.classList.remove(
      'rounded-b',
      'md:rounded-b-none',
      'md:rounded-b-none'
    )
    this.elements.button.classList.add('rounded')
  }

  /**
   * Handle the Escape key press to hide the list and focus on the button.
   * @param {KeyboardEvent} event - The keyup event object.
   */
  onLocalizationFormKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return
    this.hideList()
    this.elements.button.focus()
  }

  /**
   * Handle the click event on a list item to update the input value and submit the form.
   * @param {MouseEvent} event - The click event object.
   */
  onItemClick(event) {
    event.preventDefault()
    const form = this.querySelector('form')
    this.elements.input.value = event.currentTarget.dataset.value
    if (form) form.submit()
  }

  /**
   * Toggle the visibility of the dropdown list and update button attributes and classes.
   */
  toggleList() {
    this.elements.list.toggleAttribute('hidden')
    this.elements.button.classList.toggle('rounded-b')
    this.elements.button.classList.toggle('md:rounded-t')
    this.elements.button.classList.toggle('md:rounded-b-none')
    this.elements.button.setAttribute(
      'aria-expanded',
      (
        this.elements.button.getAttribute('aria-expanded') === 'false'
      ).toString()
    )
  }

  /**
   * Handle the focusout event on the button to hide the list if focus is lost.
   * @param {FocusEvent} event - The focusout event object.
   */
  onButtonFocusOut(event) {
    const disclosureLostFocus = this.contains(event.relatedTarget) === false
    if (disclosureLostFocus) {
      this.hideList()
    }
  }

  /**
   * Handle the focusout event on the list to hide the list if focus is lost.
   * @param {FocusEvent} event - The focusout event object.
   */
  onListFocusOut(event) {
    const childInFocus = event.currentTarget.contains(event.relatedTarget)
    if (!childInFocus) {
      this.hideList()
    }
  }

  /**
   * Handle clicks outside the dropdown to hide the list if necessary.
   * @param {MouseEvent} event - The click event object.
   */
  onBodyClick(event) {
    const isOption = this.contains(event.target)
    const isVisible =
      this.elements.button.getAttribute('aria-expanded') === 'true'
    if (isVisible && !isOption) {
      this.hideList()
    }
  }
}

// Register the custom element 'localization-form'
window.customElements.define('localization-form', LocalizationForm)
